import React, { useEffect, useState, useRef } from "react";
import { styled, keyframes } from "@mui/material/styles";

interface NotificationBubbleProps {
  count: number;
  showZero?: boolean;
}

// Animation keyframes for pulse effect
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

// Animation for number change (fade and scale)
const numberChangeAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Glow animation for attention-grabbing effect
const glowAnimation = keyframes`
  0% {
    box-shadow: 0 2px 4px rgba(0,0,0,0.2), 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  50% {
    box-shadow: 0 2px 4px rgba(0,0,0,0.2), 0 0 0 4px rgba(220, 53, 69, 0.4);
  }
  100% {
    box-shadow: 0 2px 4px rgba(0,0,0,0.2), 0 0 0 0 rgba(220, 53, 69, 0);
  }
`;

const StyledBubble = styled("span")<{ animate: boolean }>(({ animate }) => ({
  backgroundColor: "#dc3545", // Red background
  color: "#ffffff", // White text
  minWidth: "20px",
  height: "20px",
  borderRadius: "10px",
  fontSize: "11px",
  fontWeight: 600,
  padding: "0 6px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  lineHeight: "20px",
  marginLeft: "4px",
  transition: "transform 0.2s ease-in-out",
  animation: animate
    ? `${pulseAnimation} 0.5s ease-in-out, ${glowAnimation} 0.5s ease-in-out`
    : "none",
}));

const StyledCount = styled("span")<{ animate: boolean }>(({ animate }) => ({
  display: "inline-block",
  animation: animate ? `${numberChangeAnimation} 0.3s ease-out` : "none",
}));

/**
 * NotificationBubble component displays a red badge with count
 * Shows "99+" if count exceeds 99
 * Hides if count is 0 (unless showZero is true)
 * Animates when count changes
 */
const NotificationBubble: React.FC<NotificationBubbleProps> = ({
  count,
  showZero = false,
}) => {
  const [animate, setAnimate] = useState(false);
  const prevCountRef = useRef(count);
  const isFirstRender = useRef(true);

  // Trigger animation when count changes
  useEffect(() => {
    // Skip animation on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevCountRef.current = count;
      return;
    }

    // Trigger animation on any count change
    if (count !== prevCountRef.current) {
      prevCountRef.current = count;

      // Reset animation state first, then trigger animation
      setAnimate(false);
      
      // Use requestAnimationFrame to ensure smooth animation trigger
      const rafId = requestAnimationFrame(() => {
        setAnimate(true);
      });
      
      const timer = setTimeout(() => {
        setAnimate(false);
      }, 500); // Match animation duration
      
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timer);
      };
    }
  }, [count]);

  // Don't show if count is 0 and showZero is false
  if (count === 0 && !showZero) {
    return null;
  }

  // Display "99+" if count exceeds 99
  const displayCount = count > 99 ? "99+" : count.toString();

  return (
    <StyledBubble animate={animate}>
      <StyledCount animate={animate} key={count}>
        {displayCount}
      </StyledCount>
    </StyledBubble>
  );
};

export default NotificationBubble;

